import {
  clearSessions,
  listSessions,
  prune,
  putSession,
  titleOf,
  type StoredSession,
} from '@blooq/chat-store'
import type { StaticOption, Step } from '@blooq/flow'
import { IonButton, IonToast } from '@ionic/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  goBack,
  sendStep,
  startSession,
  type FlowFailure,
  type StepView,
} from '../lib/api'
import { applyStatusBar, commitTap, outcome, tap, watchBackButton } from '../lib/native'
import { applyTenantTheme } from '../lib/theme'
import { askOf } from '../lib/walk'

import { AskBar, FrontDoorStage, Suggestions, useAsk } from './FrontDoor'
import { Shell } from './Shell'
import { useFieldWalk } from './steps/FieldWalk'
import { StepDock, type Echo } from './steps/StepDock'
import { TerminalActions, isMessageTerminal, terminalText } from './steps/TerminalDock'
import { Avatar, Bubble, PastTurn, Skeleton, Typing } from './Transcript'

/*
 * The renderer.
 *
 * Its entire job is: draw the step it was handed, collect one answer, post it,
 * draw whatever comes back. There is no branching logic here and there must
 * never be — the moment a component decides what comes NEXT, the WhatsApp
 * renderer needs its own copy of that decision and packages/flow stops paying
 * for itself.
 *
 * If you find yourself reaching for `if (step.id === ...)` to route, the branch
 * belongs in packages/flow/src/canonical.ts instead.
 *
 * It does decide how a step LOOKS, and it decides that from the shape of the
 * data rather than from step ids: `source.kind` already says whether a choice
 * is a list of dates, times or services; a form's `fields` say how many
 * questions to ask one by one; an empty history says the prompt is a greeting.
 */

/** One line of the transcript: a prompt, and the answer to it if there was one. */
export interface Turn {
  stepId: string
  prompt: string
  answer?: string
  /** Asked and deliberately left blank. Shown as such, so it does not read as unanswered. */
  skipped?: boolean
}

function promptOf(step: Step): string {
  return 'prompt' in step ? step.prompt : ''
}

/**
 * What the business said on this step.
 *
 * Usually the step's prompt. A step with no prompt of its own is one whose
 * reply is data — the stored FAQ answer the matcher found — and the runtime
 * hands that over in the view's result. Decided from the shape of the view,
 * not from the step's id.
 */
function spokenOf(view: StepView): string {
  const prompt = promptOf(view.step)
  if (prompt) return prompt
  const answer = view.result?.faqAnswer
  return answer ? String(answer) : ''
}

/**
 * What a customer is told, per failure.
 *
 * `not_serving` says nothing about why, and that is deliberate — the business
 * is our customer, not this reader, and "trial expired" on a public screen is
 * us discussing their account with whoever scanned their QR code.
 */
function messageFor(failure: FlowFailure): string {
  switch (failure.kind) {
    case 'not_serving':
    case 'not_found':
      return 'This app isn’t available right now. Please contact the business directly.'
    case 'rate_limited':
      return 'That was a lot of taps at once. Give it a moment and try again.'
    case 'expired':
    case 'unavailable':
      return 'We couldn’t reach the business just now. Check your connection and try again.'
  }
}

export function Conversation({ slug }: { slug: string }) {
  const [view, setView] = useState<StepView | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [busy, setBusy] = useState(true)
  /** Unrecoverable: the conversation is replaced by this and a retry button. */
  const [fatal, setFatal] = useState<string | null>(null)
  /** Recoverable: the step stays on screen and the customer can tap again. */
  const [toast, setToast] = useState<string | null>(null)

  const [sessions, setSessions] = useState<StoredSession[]>([])
  /** A past conversation being read back. Null means the live one. */
  const [reading, setReading] = useState<StoredSession | null>(null)

  /** Keeps a conversation's start time stable across its own updates. */
  const startedAt = useRef<Record<string, number>>({})

  /*
   * Where a form step is up to.
   *
   * Lives here and not in the dock because both halves of the screen read it:
   * the transcript prints the questions already answered, the dock asks the
   * next one, and the back gesture steps through them before it steps through
   * the flow.
   */
  const walk = useFieldWalk(view)

  const refreshSessions = useCallback(async () => {
    setSessions(await listSessions(slug))
  }, [slug])

  useEffect(() => {
    void refreshSessions()
  }, [refreshSessions])

  /*
   * The server hands back one step at a time and keeps no transcript, so the
   * conversation above the dock is assembled here.
   *
   * Advancing pushes the step just left, with what was answered. A step asked
   * in one tap is one line; a form asked field by field is the step's prompt
   * and then one line per question, so the log reads exactly as the screen
   * did. Going back is detected by the returning step already being in the
   * log — we truncate to its first line rather than guessing how many turns a
   * `back` edge unwound, which only the flow definition knows.
   */
  const record = useCallback((previous: StepView | null, next: StepView, echo?: Echo) => {
    setTurns((log) => {
      if (!previous) return log

      // A rejected submission comes back as the same step carrying errors.
      // That is a retry, not a turn — logging it prints the prompt twice with
      // the failed answer wedged between the copies.
      if (previous.step.id === next.step.id) return log

      const seen = log.findIndex((turn) => turn.stepId === next.step.id)
      if (seen !== -1) return log.slice(0, seen)

      const stepId = previous.step.id
      if (Array.isArray(echo)) {
        return [...log, ...echo.map((line) => ({ stepId, ...line }))]
      }
      /*
       * The front door draws no greeting, so the turn that leaves it records
       * none either: a transcript that opens with a line the customer never
       * saw is a transcript of a different conversation.
       */
      const prompt = log.length === 0 ? '' : spokenOf(previous)
      return [...log, { stepId, prompt, answer: echo }]
    })
  }, [])

  const start = useCallback(async () => {
    setBusy(true)
    setReading(null)
    const result = await startSession(slug)
    if (result.ok) {
      setView(result.view)
      setTurns([])
      setFatal(null)
    } else {
      setFatal(messageFor(result.failure))
    }
    setBusy(false)
  }, [slug])

  useEffect(() => {
    void start()
  }, [start])

  const send = useCallback(
    async (payload: Record<string, unknown>, echo?: Echo) => {
      if (!view || busy) return
      tap()
      setBusy(true)

      const result = await sendStep(view.sessionId, payload)

      if (result.ok) {
        record(view, result.view, echo)
        setView(result.view)
        // The one moment worth a real buzz: an appointment now exists.
        if (result.view.step.type === 'terminal' && result.view.result?.ref) outcome('success')
      } else if (result.failure.kind === 'expired') {
        /*
         * The session aged out mid-conversation, or was started against an
         * older flow version. Silently opening a new one is kinder than an
         * error the customer cannot act on.
         */
        setBusy(false)
        return void start()
      } else if (result.failure.kind === 'not_serving' || result.failure.kind === 'not_found') {
        // The subscription gate closing while somebody was choosing a time.
        // Retrying cannot help, so the conversation ends here.
        outcome('error')
        setFatal(messageFor(result.failure))
      } else {
        /*
         * A blip — a tunnel, a lift, a lost packet.
         *
         * The step STAYS on screen and the customer taps again. The web
         * renderer replaces the conversation with an error here, which on a
         * desktop broadband connection is a rare event and on a phone is
         * Tuesday: throwing away six answers because a request timed out is
         * the worst thing this app could do to someone standing in a queue.
         */
        outcome('warning')
        setToast(messageFor(result.failure))
      }

      setBusy(false)
    },
    [view, busy, record, start],
  )

  const back = useCallback(async () => {
    if (!view || busy) return
    tap()
    setBusy(true)
    const result = await goBack(view.sessionId)
    if (result.ok) {
      record(view, result.view)
      setView(result.view)
    } else if (result.failure.kind === 'expired') {
      setBusy(false)
      return void start()
    } else {
      setToast(messageFor(result.failure))
    }
    setBusy(false)
  }, [view, busy, record, start])

  /*
   * The business's colours, applied to the app.
   *
   * Keyed on the values rather than on `tenant`, which is a fresh object in
   * every response — depending on the object would repaint the status bar and
   * regenerate the icon on every single step.
   */
  const accent = view?.tenant.theme?.accent
  const brand = `${view?.tenant.businessName ?? ''}|${accent ?? ''}|${view?.tenant.logoUrl ?? ''}`
  const chrome = view?.tenant
  useEffect(() => {
    if (!chrome) return
    applyTenantTheme(chrome)
    if (accent) void applyStatusBar(accent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand])

  /*
   * Persist after every turn.
   *
   * Keyed on `turns` rather than on `view`: a step returning with a validation
   * error is a new view and not a new turn, and writing on every view rewrites
   * the same record on each failed submit.
   *
   * Nothing is written until there is at least one completed turn, so a
   * customer who opens the app and leaves does not litter the list with empty
   * conversations.
   */
  useEffect(() => {
    if (!view || !turns.length) return

    const id = view.sessionId
    startedAt.current[id] ??= Date.now()

    void putSession({
      id,
      slug,
      title: titleOf(turns),
      turns: turns.map((turn) => ({ prompt: turn.prompt, answer: turn.answer })),
      startedAt: startedAt.current[id]!,
      updatedAt: Date.now(),
      done: view.step.type === 'terminal',
    })
      .then(() => prune(slug))
      .then(refreshSessions)
  }, [view, turns, slug, refreshSessions])

  /*
   * Back means the nearest thing to undo.
   *
   * Two questions into a form, that is the previous question — a local move,
   * no round trip, nothing lost. On a form's first question, or any other
   * step, it is the flow's own back edge. The header button and the hardware
   * gesture agree on this, because they call the same two functions.
   */
  const canGoBack = Boolean(view && 'back' in view.step && view.step.back)
  const canUnwindWalk = walk.index > 0
  const backAction = useRef<() => boolean>(() => false)
  backAction.current = () => {
    if (reading) {
      setReading(null)
      return true
    }

    if (canUnwindWalk) return walk.back()

    /*
     * Nothing to unwind: the first step, or a terminal one. Declining lets the
     * gesture do what it means — close the app, or leave the page.
     */
    if (!canGoBack) return false

    /*
     * Mid-request, and this is CONSUMED rather than declined.
     *
     * A step is in flight for a couple of hundred milliseconds, and a back
     * press that lands inside that window used to fall through to "nothing to
     * unwind" — which now closes the app. Swallowing it costs the customer one
     * ignored press; declining it costs them the booking.
     */
    if (busy) return true

    void back()
    return true
  }

  /*
   * The back gesture — hardware button, Android's swipe, or the Escape key.
   *
   * All three arrive as the same Ionic event; watchBackButton in lib/native.ts
   * is where that is arranged, and Ionic's own handlers have already closed any
   * open drawer or sheet by the time this runs.
   *
   * Held in a ref because the listener is registered once and the decision it
   * makes depends on state that changes every step — re-registering on each
   * render would leak listeners, and capturing the first render's state would
   * make the gesture answer questions about a step the customer left long ago.
   */
  useEffect(() => watchBackButton(() => backAction.current()), [])

  const onSend = useCallback(
    (payload: Record<string, unknown>, echo?: Echo) => {
      // A heavier tick for the tap that commits. `send` fires the light one for
      // everything else.
      if (view?.step.type === 'review') commitTap()
      void send(payload, echo)
    },
    [send, view],
  )

  /*
   * The front door.
   *
   * No history, no way back, and a menu to choose from: this is a conversation
   * that has not started, and it opens as the business's welcome rather than
   * as a bubble at the bottom of an empty screen. Decided from the shape of
   * the moment — not from the step's id, which a restarted flow may or may not
   * share. "Back to menu" at the end of a branch empties the log and lands
   * here again.
   */
  const opening =
    Boolean(view) && !reading && turns.length === 0 && !canGoBack && view?.step.type === 'choice'

  /*
   * A step that takes a sentence keeps the box on screen, and a step whose
   * choices are the business's menu shows them as suggestions under the
   * reply. Both read off the step's own shape — its `freeText` edge, its
   * option source — and neither off its id.
   */
  const asking = Boolean(view && view.step.type === 'choice' && view.step.freeText)
  const menuLike = Boolean(
    view && view.step.type === 'choice' && view.step.source.kind === 'industryMenu',
  )

  /*
   * The sentence being composed, typed or dictated.
   *
   * Posted as `{ text }` and nothing else: the step declares where a sentence
   * goes (its `freeText` edge, to the FAQ matcher) and the server walks it.
   * This renderer never picks an option on the customer's behalf.
   */
  const ask = useAsk({
    active: asking && !reading,
    resetKey: `${view?.sessionId ?? ''}:${turns.length}`,
    onSend: (text) => onSend({ text }, text),
    onNotice: setToast,
  })

  const pick = (option: StaticOption) => onSend({ value: option.value }, option.label)

  const shell = {
    tenant: view?.tenant,
    sessions,
    currentId: reading?.id ?? view?.sessionId,
    onOpenSession: (session: StoredSession) => setReading(session),
    onNewConversation: () => void start(),
    onClearHistory: () => {
      setReading(null)
      void clearSessions(slug).then(refreshSessions)
    },
  }

  const avatar = <Avatar tenant={view?.tenant} />

  if (fatal) {
    return (
      <Shell {...shell}>
        {{
          transcript: (
            <Bubble kind="bot" avatar={avatar}>
              {fatal}
            </Bubble>
          ),
          dock: (
            <IonButton expand="block" onClick={() => void start()}>
              Try again
            </IonButton>
          ),
        }}
      </Shell>
    )
  }

  if (!view) {
    return (
      <Shell {...shell}>
        {{ transcript: <Skeleton />, dock: null }}
      </Shell>
    )
  }

  /*
   * Reading a past conversation.
   *
   * Shown as it was and not continuable: the server keeps no transcript and its
   * session will long since have aged out, so offering a composer here would be
   * a text box that silently could not send.
   */
  if (reading) {
    return (
      <Shell {...shell} reading onBack={() => setReading(null)}>
        {{
          transcript: (
            <>
              <div className="blooq-note">
                A conversation from{' '}
                {new Date(reading.startedAt).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                })}
                . It cannot be continued.
              </div>
              {reading.turns.map((turn, index) => (
                <PastTurn
                  key={index}
                  turn={{ stepId: String(index), ...turn }}
                  avatar={avatar}
                />
              ))}
            </>
          ),
          dock: (
            <IonButton expand="block" onClick={() => void start()}>
              Start a new conversation
            </IonButton>
          ),
        }}
      </Shell>
    )
  }

  /*
   * A reply with buttons.
   *
   * An FAQ answer or a "we couldn't find that" is a sentence with two or three
   * things to do next, and the place for those is under the sentence — the
   * way a messaging app attaches buttons to a reply — not in a dock the text
   * has nothing to do with. The dock is empty for these steps on purpose.
   */
  const message = isMessageTerminal(view)

  return (
    <>
      <Shell
        {...shell}
        onBack={
          canUnwindWalk ? () => void walk.back() : canGoBack ? () => void back() : undefined
        }
        scrollKey={`${view.step.id}:${turns.length}:${walk.index}:${busy}`}
      >
        {{
          transcript: (
            <>
              {turns.map((turn, index) => (
                <PastTurn key={`${turn.stepId}-${index}`} turn={turn} avatar={avatar} />
              ))}

              {opening ? (
                <FrontDoorStage view={view} ask={ask} busy={busy} onPick={pick} />
              ) : walk.current ? (
                <>
                  {walk.exchanges.map((line, index) => (
                    <PastTurn
                      key={`${view.step.id}-q${index}`}
                      turn={{ stepId: view.step.id, ...line }}
                      avatar={avatar}
                    />
                  ))}
                  <Bubble kind="bot" lead avatar={avatar}>
                    {askOf(walk.current)}
                    {!walk.current.required ? (
                      <span className="blooq-bubble__hint">Optional</span>
                    ) : null}
                  </Bubble>
                </>
              ) : message ? (
                <Bubble kind="bot" lead card avatar={avatar}>
                  <p className="blooq-bubble__text">{terminalText(view)}</p>
                  <TerminalActions view={view} busy={busy} onSend={onSend} inline />
                </Bubble>
              ) : (
                <>
                  <Bubble kind="bot" lead avatar={avatar}>
                    {spokenOf(view)}
                  </Bubble>
                  {/* The reply's suggestions: the menu, under the words,
                      the way an assistant offers what it can do next. */}
                  {menuLike && !busy ? (
                    <Suggestions options={view.options} disabled={ask.listening} onPick={pick} />
                  ) : null}
                </>
              )}

              {/* The front door's sphere already says "thinking". */}
              {busy && !opening ? (
                <Typing avatar={avatar} accent={view.tenant.theme?.accent} />
              ) : null}
            </>
          ),
          dock: opening || asking ? (
            /* A sentence the matcher refused ("type your question") comes
               back against the menu step under the lookup's own key. */
            <AskBar ask={ask} busy={busy} error={view.errors?.question ?? view.errors?.value} />
          ) : message ? null : (
            <StepDock
              key={view.step.id}
              view={view}
              busy={busy}
              walk={walk}
              onSend={onSend}
              onReplace={setView}
              onNotice={setToast}
            />
          ),
        }}
      </Shell>

      <IonToast
        cssClass="blooq-toast"
        isOpen={toast !== null}
        message={toast ?? ''}
        duration={4000}
        position="top"
        onDidDismiss={() => setToast(null)}
      />
    </>
  )
}
